import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Side from './Side';
import { Sidebar } from "../components/sidebar";
import { Breadcrumb, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from "../Context/AuthContext";
import { BASE_URL } from '../constants';

const fetchNameById = async (id) => {
  try {
    const response = await fetch(`${BASE_URL}/course/${id}`);
    if (!response.ok) {
      throw new Error('Failed to fetch course name');
    }
    const data = await response.json();
    return data.name;
  } catch (error) {
    console.error('Error fetching course name:', error);
    return null;  // Returning null if fetching fails
  }
};

const Layout = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dynamicName, setDynamicName] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const pathnames = location.pathname.split('/').filter((x) => x);
    const id = pathnames[pathnames.length - 1];

    if (id && id.match(/[0-9a-fA-F]{24}/)) {
      fetchNameById(id).then((name) => {
        setDynamicName(name);
        setLoading(false);
      });
    } else {
      setDynamicName(null);
      setLoading(false);
    }
  }, [location.pathname]);

  const toggle = () => {
    setIsOpen(!isOpen);
  };

  const getBreadcrumb = () => {
    const pathnames = location.pathname.split('/').filter(Boolean);
    const dashboardPaths = { '/admin/dashboard': 'Admin Dashboard', '/dashboard': 'Dashboard', '/state/dashboard': 'State Dashboard' };

    // Handle dashboard paths
    if (dashboardPaths[location.pathname]) {
      return (
        <Breadcrumb className="d-md-inline-block mt-2 mx-2" listProps={{ className: "breadcrumb-dark breadcrumb-transparent" }}>
          <Breadcrumb.Item onClick={() => navigate('/')}><FontAwesomeIcon icon={faHome} /></Breadcrumb.Item>
          <Breadcrumb.Item active>{dashboardPaths[location.pathname]}</Breadcrumb.Item>
        </Breadcrumb>
      );
    }

    // Handle notification-related paths
    if (pathnames.includes("notification") && pathnames.length > 1) {
      return (
        <Breadcrumb className="d-md-inline-block mt-2 mx-2" listProps={{ className: "breadcrumb-dark breadcrumb-transparent" }}>
          <Breadcrumb.Item onClick={() => navigate('/')}><FontAwesomeIcon icon={faHome} /></Breadcrumb.Item>
          {user?.isAdmin && <Breadcrumb.Item onClick={() => navigate('/admin/dashboard')}>Admin Dashboard</Breadcrumb.Item>}
          {user?.isState && <Breadcrumb.Item onClick={() => navigate('/state/dashboard')}>State Dashboard</Breadcrumb.Item>}
          {user?.isRefugee && <Breadcrumb.Item onClick={() => navigate('/dashboard')}>Dashboard</Breadcrumb.Item>}
        </Breadcrumb>
      );
    }

    // Filter out "state" path if the user is on the state route
    const filteredPathnames = ['admin', 'state'].includes(pathnames[0]) ? pathnames.slice(1) : pathnames;

    // Only apply "state" path and ID filtering for user.isState
    const finalPathnames = user?.isState
      ? filteredPathnames.filter((part) => part !== "state" && !/^[0-9a-fA-F]{24}$/.test(part) && !/^\d+$/.test(part))
      : filteredPathnames;

    return (
      <Breadcrumb className="d-md-inline-block mt-2 mx-2" listProps={{ className: "breadcrumb-dark breadcrumb-transparent" }}>
        <Breadcrumb.Item onClick={() => navigate('/')}><FontAwesomeIcon icon={faHome} /></Breadcrumb.Item>

        {/* Show user-specific breadcrumbs */}
        {user?.isState && <Breadcrumb.Item onClick={() => navigate('/state/dashboard')}>State Dashboard</Breadcrumb.Item>}
        {user?.isAdmin && <Breadcrumb.Item onClick={() => navigate('/admin/dashboard')}>Admin Dashboard</Breadcrumb.Item>}
        {user?.isRefugee && <Breadcrumb.Item onClick={() => navigate('/dashboard')}>Dashboard</Breadcrumb.Item>}

        {/* Generate dynamic breadcrumb items, excluding ID */}
        {finalPathnames.map((name, index) => {
          const path = finalPathnames.slice(0, index + 1).join('/');
          const navigatePath = user?.isState && index === 0 ? `/state/${path}` : `/${path}`;

          // Replace course ID with dynamic course name for refugees
          const breadcrumbName = user?.isRefugee && index === finalPathnames.length - 1 && dynamicName
            ? dynamicName
            : name.charAt(0).toUpperCase() + name.slice(1);

          return (
            <Breadcrumb.Item
              key={index}
              active={index === finalPathnames.length - 1}
              onClick={() => navigate(navigatePath)}
            >
              {index === finalPathnames.length - 1 && loading ? <Spinner animation="border" size="sm" /> : breadcrumbName}
            </Breadcrumb.Item>
          );
        })}
      </Breadcrumb>
    );
  };

  return (
    <div>
      <Sidebar isOpen={isOpen} isHomePage={isHomePage} toggle={toggle} />
      <Side toggle={toggle} />

      {!user?.isRefugee ? (
        <main className="content">
          <div className="content-wrapper">
            {getBreadcrumb()}
            {children}
          </div>
        </main>
      ) : (
        <main>
          <div className="content-wrapper">
            {getBreadcrumb()}
            {children}
          </div>
        </main>
      )}
    </div>
  );
};

export default Layout;
